import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { CheckCircle, XCircle, Clock, AlertCircle, Calendar as CalendarIcon } from "lucide-react";
import type { HospitalPartner, HealthcareProvider, PaymentPlan, HospitalPartnerStats } from "@shared/schema";
import { z } from "zod";

export interface DashboardData {
  partner: HospitalPartner;
  provider: HealthcareProvider | null;
  stats: HospitalPartnerStats[];
  paymentPlans: PaymentPlan[];
}

export const partnerFormSchema = z.object({
  healthcareProviderId: z.string().min(1, "Healthcare provider is required"),
  partnerName: z.string().min(1, "Partner name is required"),
  contactName: z.string().optional().default(""),
  contactEmail: z.string().email("Please enter a valid email address").or(z.literal("")).optional().default(""),
  contactPhone: z.string().optional().default(""),
  status: z.enum(["active", "pending", "suspended", "inactive"]).default("pending"),
  paymentTermsDays: z.coerce.number().int().min(1, "Must be at least 1 day").default(30),
  revenueSharePercent: z.coerce.number().min(0, "Cannot be negative").max(100, "Cannot exceed 100%").default(0),
  monthlyMinimumOrders: z.coerce.number().int().min(0, "Cannot be negative").default(0),
  contractStartDate: z.date().optional(),
  contractEndDate: z.date().optional(),
  notes: z.string().optional().default(""),
}).refine((data) => {
  if (data.contractStartDate && data.contractEndDate) {
    return data.contractEndDate > data.contractStartDate;
  }
  return true;
}, {
  message: "End date must be after start date",
  path: ["contractEndDate"],
});

export type PartnerFormValues = z.infer<typeof partnerFormSchema>;

export function StatusBadge({ status }: { status: string }) {
  const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
    active: "default",
    pending: "secondary",
    suspended: "destructive",
    inactive: "outline",
  };
  const icons: Record<string, JSX.Element> = {
    active: <CheckCircle className="h-3 w-3 mr-1" />,
    pending: <Clock className="h-3 w-3 mr-1" />,
    suspended: <XCircle className="h-3 w-3 mr-1" />,
    inactive: <AlertCircle className="h-3 w-3 mr-1" />,
  };
  return (
    <Badge variant={variants[status] || "secondary"} data-testid={`badge-status-${status}`} className="flex items-center">
      {icons[status]}
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
}

export function DatePickerField({ 
  label, 
  value, 
  onChange, 
  placeholder,
  testId 
}: { 
  label: string; 
  value: Date | undefined; 
  onChange: (date: Date | undefined) => void;
  placeholder?: string;
  testId: string;
}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="w-full justify-start text-left font-normal"
            data-testid={testId}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {value ? format(value, "PPP") : <span className="text-muted-foreground">{placeholder || "Pick a date"}</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={value}
            onSelect={onChange}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

export function exportPartnersToCsv(partners: HospitalPartner[], t: (key: string, fallback: string) => string) {
  const headers = [
    t('hospitalPartners.partnerCode', 'Partner Code'),
    t('hospitalPartners.partnerName', 'Partner Name'),
    t('hospitalPartners.status', 'Status'),
    t('hospitalPartners.contactName', 'Contact Name'),
    t('hospitalPartners.contactEmail', 'Email'),
    t('hospitalPartners.contactPhone', 'Phone'),
    t('hospitalPartners.totalOrders', 'Total Orders'),
    t('hospitalPartners.totalDeliveries', 'Deliveries'),
    t('hospitalPartners.paymentPlans', 'Payment Schedules'),
    t('hospitalPartners.revenue', 'Revenue'),
    t('hospitalPartners.paymentTerms', 'Payment Terms'),
    t('hospitalPartners.revenueShare', 'Revenue Share %'),
    t('hospitalPartners.contractStart', 'Contract Start'),
    t('hospitalPartners.contractEnd', 'Contract End'),
  ];
  
  const rows = partners.map(p => [
    p.partnerCode,
    p.partnerName,
    p.status,
    p.contactName || '',
    p.contactEmail || '',
    p.contactPhone || '',
    p.totalOrders || 0,
    p.totalDeliveries || 0,
    p.totalPaymentPlans || 0,
    ((p.totalRevenue || 0) / 100).toFixed(2),
    `NET ${p.paymentTermsDays}`,
    p.revenueSharePercent,
    p.contractStartDate ? format(new Date(p.contractStartDate), 'yyyy-MM-dd') : '',
    p.contractEndDate ? format(new Date(p.contractEndDate), 'yyyy-MM-dd') : '',
  ]);
  
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
  ].join('\n');
  
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `hospital-partners-${format(new Date(), 'yyyy-MM-dd')}.csv`;
  link.click();
}
